import { PantallaCargando } from '@/components/Cargando';
import { useState, useEffect } from 'react';
import api, { formatApiError } from '@/lib/api';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { CreditCard, History, Plus, Trash2, Shield, ArrowUpDown } from 'lucide-react';
import { useParams, useNavigate } from 'react-router-dom';
import { PLAN_GROUPS, LIMIT_LABELS } from '@/lib/planLabels';

export default function TenantSubscription() {
  const { id: tenantId } = useParams();
  const navigate = useNavigate();
  const [tenant, setTenant] = useState(null);
  const [sub, setSub] = useState(null);
  const [plans, setPlans] = useState([]);
  const [addons, setAddons] = useState([]);
  const [overrides, setOverrides] = useState([]);
  const [history, setHistory] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showAssignAddon, setShowAssignAddon] = useState(false);
  const [showOverride, setShowOverride] = useState(false);
  const [overrideForm, setOverrideForm] = useState({ limit_key: '', override_value: 0, reason: '' });
  const [selectedAddon, setSelectedAddon] = useState('');

  const fetchData = async () => {
    setLoading(true);
    try {
      const [tRes, sRes, pRes, aRes, oRes, hRes] = await Promise.all([
        api.get(`/admin/tenants/${tenantId}`),
        api.get(`/admin/tenants/${tenantId}/subscription`),
        api.get('/admin/plans/catalog?status=active'),
        api.get('/admin/addons?status=active'),
        api.get(`/admin/tenants/${tenantId}/overrides`),
        api.get(`/admin/tenants/${tenantId}/plan-history`),
      ]);
      setTenant(tRes.data);
      setSub(sRes.data);
      setPlans(pRes.data);
      setAddons(aRes.data);
      setOverrides(oRes.data);
      setHistory(hRes.data);
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setLoading(false); }
  };

  useEffect(() => { fetchData(); }, [tenantId]);

  const handleAssignPlan = async (planId) => {
    try {
      await api.put(`/admin/tenants/${tenantId}/subscription`, {
        plan_id: planId, billing_cycle: 'monthly', status: 'active', payment_status: 'pending',
      });
      toast.success('Plan asignado');
      fetchData();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const handleChangeStatus = async (newStatus) => {
    try {
      await api.put(`/admin/tenants/${tenantId}/subscription`, { status: newStatus });
      toast.success(`Estado cambiado a ${newStatus}`);
      fetchData();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const handleStartTrial = async () => {
    const now = new Date();
    const end = new Date(now.getTime() + 14 * 24 * 60 * 60 * 1000);
    try {
      await api.put(`/admin/tenants/${tenantId}/subscription`, {
        status: 'trialing', trial_start: now.toISOString(), trial_end: end.toISOString(),
      });
      toast.success('Trial activado (14 días)');
      fetchData();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const handleAssignAddon = async () => {
    if (!selectedAddon) return;
    try {
      await api.post(`/admin/tenants/${tenantId}/addons`, { addon_id: selectedAddon, quantity: 1 });
      toast.success('Add-on asignado');
      setShowAssignAddon(false);
      fetchData();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const handleRemoveAddon = async (addonId) => {
    try {
      await api.delete(`/admin/tenants/${tenantId}/addons/${addonId}`);
      toast.success('Add-on removido');
      fetchData();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const handleCreateOverride = async () => {
    if (!overrideForm.limit_key) { toast.error('Clave de límite requerida'); return; }
    try {
      await api.put(`/admin/tenants/${tenantId}/overrides`, overrideForm);
      toast.success('Override creado');
      setShowOverride(false);
      setOverrideForm({ limit_key: '', override_value: 0, reason: '' });
      fetchData();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  if (loading) return <PantallaCargando />;

  const statusColors = {
    active: 'bg-emerald-50 text-emerald-600', trialing: 'bg-blue-50 text-blue-600',
    suspended: 'bg-red-50 text-red-600', cancelled: 'bg-[#F1F5F9] text-[#94A3B8]',
    past_due: 'bg-amber-50 text-amber-600', no_subscription: 'bg-[#F1F5F9] text-[#94A3B8]',
  };
  const statusLabels = {
    active: 'Activo', trialing: 'En prueba', suspended: 'Suspendido',
    cancelled: 'Cancelado', past_due: 'Pago vencido', no_subscription: 'Sin plan',
  };

  return (
    <div data-testid="tenant-subscription-page">
      <div className="flex items-center justify-between mb-6">
        <div>
          <button onClick={() => navigate(`/admin/tenants/${tenantId}`)} className="text-xs text-[#94A3B8] hover:text-[#0F172A] mb-1 block">&larr; Volver al tenant</button>
          <h1 className="text-2xl font-bold text-[#0F172A]">{tenant?.name}</h1>
          <p className="text-sm text-[#64748B]">Gestión de Suscripción</p>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Current Plan */}
        <div className="lg:col-span-2 space-y-4">
          <div className="bg-white border border-[#E2E8F0] rounded-xl p-5">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-sm font-semibold text-[#0F172A] flex items-center gap-2"><CreditCard className="w-4 h-4" /> Plan Actual</h3>
              <span className={`text-xs px-2.5 py-1 rounded-full ${statusColors[sub?.status] || statusColors.no_subscription}`} data-testid="sub-status">{statusLabels[sub?.status] || 'Sin plan'}</span>
            </div>
            {sub?.plan_name ? (
              <div>
                <p className="text-lg font-bold text-[#0F172A]" data-testid="current-plan-name">{sub.plan_name}</p>
                <p className="text-xs text-[#64748B] mt-1">Ciclo: {sub.billing_cycle} | Pago: {sub.payment_status}</p>
                {sub.trial_end && <p className="text-xs text-blue-500 mt-1">Trial hasta: {new Date(sub.trial_end).toLocaleDateString()}</p>}
                {sub.renewal_date && <p className="text-xs text-[#64748B] mt-1">Renovación: {new Date(sub.renewal_date).toLocaleDateString()}</p>}
                <FechaDeCobro sub={sub} tenantId={tenantId} onGuardado={fetchData} />
              </div>
            ) : (
              <p className="text-sm text-[#94A3B8]">Sin plan asignado</p>
            )}
            <div className="flex flex-wrap gap-2 mt-4 pt-4 border-t border-[#F1F5F9]">
              {sub?.status !== 'suspended' && <Button variant="outline" size="sm" onClick={() => handleChangeStatus('suspended')} data-testid="suspend-btn" className="text-red-500 hover:text-red-600">Suspender</Button>}
              {sub?.status === 'suspended' && <Button variant="outline" size="sm" onClick={() => handleChangeStatus('active')} data-testid="reactivate-btn" className="text-emerald-600">Reactivar</Button>}
              {sub?.status !== 'trialing' && <Button variant="outline" size="sm" onClick={handleStartTrial} data-testid="start-trial-btn">Iniciar Trial</Button>}
            </div>
          </div>

          {/* Change/Assign Plan */}
          <div className="bg-white border border-[#E2E8F0] rounded-xl p-5">
            <h3 className="text-sm font-semibold text-[#0F172A] flex items-center gap-2 mb-4"><ArrowUpDown className="w-4 h-4" /> Cambiar Plan</h3>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              {plans.map(p => (
                <button key={p.id} onClick={() => handleAssignPlan(p.id)} data-testid={`assign-plan-${p.id}`}
                  className={`text-left p-3 rounded-lg border transition-all hover:border-[#0F172A] ${sub?.plan_id === p.id ? 'border-[#0F172A] bg-[#F8F9FA]' : 'border-[#E2E8F0]'}`}>
                  <p className="text-sm font-medium text-[#0F172A]">{p.name}</p>
                  <p className="text-xs text-[#64748B]">${p.price_monthly}/mes</p>
                </button>
              ))}
            </div>
          </div>

          {/* Add-ons */}
          <div className="bg-white border border-[#E2E8F0] rounded-xl p-5">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-sm font-semibold text-[#0F172A]">Add-ons Asignados</h3>
              <Button variant="outline" size="sm" onClick={() => setShowAssignAddon(!showAssignAddon)} data-testid="assign-addon-btn"><Plus className="w-3.5 h-3.5 mr-1" /> Agregar</Button>
            </div>
            {showAssignAddon && (
              <div className="flex gap-2 mb-4 p-3 bg-[#F8F9FA] rounded-lg">
                <select className="flex-1 h-9 px-3 text-sm border border-[#E2E8F0] rounded-md" value={selectedAddon} onChange={e => setSelectedAddon(e.target.value)} data-testid="addon-select">
                  <option value="">Seleccionar add-on...</option>
                  {addons.map(a => <option key={a.id} value={a.id}>{a.name} (${a.price})</option>)}
                </select>
                <Button size="sm" onClick={handleAssignAddon} className="bg-[#0F172A] text-white">Asignar</Button>
              </div>
            )}
            {(sub?.addons || []).length === 0 ? (
              <p className="text-xs text-[#94A3B8] text-center py-3">Sin add-ons</p>
            ) : (
              <div className="space-y-2">
                {sub.addons.filter(a => a.status === 'active').map(a => (
                  <div key={a.id} className="flex items-center justify-between py-2 border-b border-[#F8F9FA]" data-testid={`tenant-addon-${a.addon_id}`}>
                    <span className="text-sm text-[#0F172A]">{a.addon_name} (x{a.quantity})</span>
                    <Button variant="ghost" size="sm" onClick={() => handleRemoveAddon(a.addon_id)}><Trash2 className="w-3.5 h-3.5 text-red-400" /></Button>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Overrides */}
          <div className="bg-white border border-[#E2E8F0] rounded-xl p-5">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-sm font-semibold text-[#0F172A] flex items-center gap-2"><Shield className="w-4 h-4" /> Overrides Manuales</h3>
              <Button variant="outline" size="sm" onClick={() => setShowOverride(!showOverride)} data-testid="create-override-btn"><Plus className="w-3.5 h-3.5 mr-1" /> Nuevo</Button>
            </div>
            {showOverride && (
              <div className="p-3 bg-[#F8F9FA] rounded-lg mb-4 space-y-2">
                <select value={overrideForm.limit_key} onChange={e => setOverrideForm(f => ({ ...f, limit_key: e.target.value }))}
                  className="w-full text-sm border border-[#E2E8F0] rounded-md px-3 h-10 bg-white" data-testid="override-key">
                  <option value="">— Elige qué límite cambiar —</option>
                  {PLAN_GROUPS.map(group => (
                    <optgroup key={group.key} label={group.label}>
                      {[...(group.limits || []), ...(group.advancedLimits || [])].map(({ key, label }) => (
                        <option key={key} value={key}>{label}</option>
                      ))}
                    </optgroup>
                  ))}
                </select>
                <Input type="number" placeholder="Valor" value={overrideForm.override_value} onChange={e => setOverrideForm(f => ({ ...f, override_value: parseInt(e.target.value) || 0 }))} data-testid="override-value" />
                <Input placeholder="Razón" value={overrideForm.reason} onChange={e => setOverrideForm(f => ({ ...f, reason: e.target.value }))} data-testid="override-reason" />
                <Button size="sm" onClick={handleCreateOverride} className="bg-[#0F172A] text-white" data-testid="save-override-btn">Crear Override</Button>
              </div>
            )}
            {overrides.filter(o => o.is_active).length === 0 ? (
              <p className="text-xs text-[#94A3B8] text-center py-3">Sin overrides activos</p>
            ) : (
              <div className="space-y-2">
                {overrides.filter(o => o.is_active).map(o => (
                  <div key={o.id} className="flex items-center justify-between py-2 border-b border-[#F8F9FA]" data-testid={`override-${o.id}`}>
                    <div>
                      <span className="text-xs text-[#64748B]">{LIMIT_LABELS[o.limit_key] || o.limit_key}</span>
                      <span className="text-sm font-medium text-[#0F172A] ml-2">{o.override_value}</span>
                    </div>
                    {o.reason && <span className="text-xs text-[#94A3B8]">{o.reason}</span>}
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>

        {/* History sidebar */}
        <div className="bg-white border border-[#E2E8F0] rounded-xl p-5 h-fit">
          <h3 className="text-sm font-semibold text-[#0F172A] flex items-center gap-2 mb-4"><History className="w-4 h-4" /> Historial de Cambios</h3>
          {history.length === 0 ? (
            <p className="text-xs text-[#94A3B8] text-center py-3">Sin historial</p>
          ) : (
            <div className="space-y-3">
              {history.map(h => (
                <div key={h.id} className="border-l-2 border-[#E2E8F0] pl-3 py-1" data-testid={`history-${h.id}`}>
                  <p className="text-xs font-medium text-[#0F172A]">{h.change_type.replace(/_/g, ' ')}</p>
                  {h.notes && <p className="text-[11px] text-[#94A3B8]">{h.notes}</p>}
                  <p className="text-[10px] text-[#CBD5E1]">{h.created_at ? new Date(h.created_at).toLocaleString() : ''}</p>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

/** Capturar la fecha de renovación, que hasta hoy solo se podía LEER.
 *
 * POR QUÉ ESTE COMPONENTE EXISTE
 * ==============================
 *
 * El aviso «Pago próximo a vencer» de Super Admin → Mensajes del sistema compara
 * contra `renewal_date`. Al revisar los 18 negocios en producción, **ninguno la
 * tenía**: el endpoint del backend siempre aceptó la fecha, pero esta pantalla
 * solo la mostraba. O sea, el aviso estaba encendido, correcto, y no podía sonar
 * nunca — y no había forma de arreglarlo desde la aplicación.
 *
 * Es la forma de defecto que ya nos costó caro antes: backend completo, pantalla
 * sin el campo. Por eso el campo va aquí, junto al plan, y no en una pantalla
 * nueva: el dueño ya viene a esta cuando cobra.
 */
function FechaDeCobro({ sub, tenantId, onGuardado }) {
  const soloFecha = (v) => (v ? new Date(v).toISOString().slice(0, 10) : '');
  const [renovacion, setRenovacion] = useState(soloFecha(sub?.renewal_date));
  const [finPeriodo, setFinPeriodo] = useState(soloFecha(sub?.current_period_end));
  const [guardando, setGuardando] = useState(false);

  const sucio = renovacion !== soloFecha(sub?.renewal_date)
    || finPeriodo !== soloFecha(sub?.current_period_end);

  const guardar = async () => {
    setGuardando(true);
    try {
      // Se manda a mediodía UTC y no a medianoche: guardado como 00:00 UTC, un
      // negocio en México ve el día ANTERIOR, y el aviso de «faltan 7 días»
      // saldría corrido un día.
      const aFecha = (v) => (v ? new Date(`${v}T12:00:00Z`).toISOString() : null);
      await api.put(`/admin/tenants/${tenantId}/subscription`, {
        renewal_date: aFecha(renovacion),
        current_period_end: aFecha(finPeriodo),
      });
      toast.success('Fechas guardadas. El aviso de cobro ya puede calcularse con ellas.');
      onGuardado?.();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally {
      setGuardando(false);
    }
  };

  return (
    <div className="mt-3 pt-3 border-t border-[#F1F5F9]">
      <p className="text-xs font-medium text-[#64748B] mb-2">Fechas de cobro</p>
      <div className="grid grid-cols-2 gap-2">
        <label className="text-[11px] text-[#64748B]">
          Se renueva el
          <input type="date" value={renovacion} onChange={(e) => setRenovacion(e.target.value)}
                 className="mt-1 w-full h-8 px-2 text-sm border border-[#E2E8F0] rounded-md"
                 data-testid="fecha-renovacion" />
        </label>
        <label className="text-[11px] text-[#64748B]">
          Termina el periodo
          <input type="date" value={finPeriodo} onChange={(e) => setFinPeriodo(e.target.value)}
                 className="mt-1 w-full h-8 px-2 text-sm border border-[#E2E8F0] rounded-md"
                 data-testid="fecha-fin-periodo" />
        </label>
      </div>
      <p className="text-[11px] text-[#64748B] mt-2">
        Sin estas fechas, el aviso «Pago próximo a vencer» no tiene contra qué comparar y
        nunca se manda, aunque esté encendido.
      </p>
      {sucio && (
        <Button size="sm" className="mt-2" onClick={guardar} disabled={guardando}
                data-testid="guardar-fechas-cobro">
          {guardando ? 'Guardando…' : 'Guardar fechas'}
        </Button>
      )}
    </div>
  );
}
